import { Link } from "react-router-dom";
import { ArrowLeft, Mail, AlertTriangle, HelpCircle, Clock, User } from "lucide-react";
import { Logo } from "@/components/Common";
import { useSeo } from "@/lib/seo";

const COMPANY = "AXON MEDIA LLC";
const PRODUCT = "Launch to Income";
const CONTACT = "admin@launchtoincome.com";
const EFFECTIVE = "July 30, 2026";
const JURISDICTION = "State of North Carolina, United States";

function LegalShell({ title, children, testid }) {
  return (
    <div className="min-h-screen bg-[#FDFCFE]" data-testid={testid}>
      <header className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
        <Link to="/"><Logo height="h-9" /></Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={15} /> Home</Link>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-slate-500">Effective date: {EFFECTIVE}</p>
        <div className="mt-8 space-y-8 text-sm leading-relaxed text-slate-700">{children}</div>
      </main>
      <footer className="mt-8 border-t border-brand-line py-8 text-center text-sm text-slate-400">
        <div className="flex items-center justify-center gap-4">
          <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-slate-700">Terms</Link>
          <span>·</span>
          <Link to="/help" className="hover:text-slate-700">Help</Link>
        </div>
        <p className="mt-3">{PRODUCT} is a product of {COMPANY}.</p>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-lg font-semibold text-slate-900">{title}</h2>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  );
}

export function Privacy() {
  useSeo({
    title: "Privacy Policy | Launch to Income",
    description: "How AXON MEDIA LLC collects, uses, and protects your information when you use Launch to Income, including AI processing, analytics, and your data rights.",
    path: "/privacy",
  });
  return (
    <LegalShell title="Privacy Policy" testid="privacy-page">
      <p>
        This Privacy Policy explains how {COMPANY} ("we," "us," or "our") collects, uses, and protects
        your information when you use {PRODUCT} (the "Service"), available at launchtoincome.com. By using
        the Service you agree to this Policy.
      </p>

      <Section title="1. Who we are">
        <p>{PRODUCT} is operated by {COMPANY}, based in Charlotte, {JURISDICTION}. For any privacy question or request, contact us at <a href={`mailto:${CONTACT}`} className="font-medium text-brand underline">{CONTACT}</a>.</p>
      </Section>

      <Section title="2. Information we collect">
        <p><strong>Account information.</strong> When you sign in with Google, we receive your name, email address, and profile picture from Google. If you register with email and password, we collect your name, email address, and a securely hashed password (we never store your password in readable form).</p>
        <p><strong>Business content you create.</strong> The answers, drafts, and business assets you build in the workspace (for example your Purpose Statement, Customer Profile, and Offer Document).</p>
        <p><strong>Product usage analytics.</strong> Privacy-first, aggregate-level events such as account created, onboarding completed, a step started or completed, an asset created, an export, and where users drop off. We use PostHog for this (see Section 5).</p>
        <p><strong>Technical data.</strong> Basic information your browser sends, such as device and browser type, needed to operate and secure the Service.</p>
        <p>We do <strong>not</strong> collect payment card details, because the Service is free during early access.</p>
      </Section>

      <Section title="3. How we use your information">
        <p>To create and secure your account; to operate the guided workspace and save your progress; to generate AI drafts you request; to understand and improve the product (which steps help, where people get stuck); and to communicate essential account messages such as email verification and password resets.</p>
        <p>We do <strong>not</strong> sell your personal information or your business content, and we do not use your business content to train third-party AI models.</p>
      </Section>

      <Section title="4. AI processing">
        <p>When you ask AI Studio to draft something, we send the relevant business context shown in the prompt to our AI provider, <strong>OpenAI</strong> (accessed through the Emergent integrations platform), to generate a draft that is returned to you. We log exactly what context was sent so you always have transparency, and each AI-assisted field is labelled as such.</p>
        <p>AI is optional. Every step can be completed by writing it yourself. You remain the author and are responsible for the content you keep.</p>
      </Section>

      <Section title="5. Analytics and cookies">
        <p><strong>Essential cookie.</strong> We use a single secure, httpOnly session cookie to keep you signed in. It is required for the Service to function.</p>
        <p><strong>Analytics (PostHog), consent-based.</strong> We use PostHog configured for privacy: no session recording, form inputs are masked, automatic click-capture is disabled, and we only record a small set of essential product events. Analytics stays paused until you accept via the consent banner, and you can decline or opt out at any time. Before you create an account, analytics is anonymous; we only associate events with your account identifier after you sign in.</p>
      </Section>

      <Section title="6. How we share information">
        <p>We share information only with service providers that help us run the Service, and only as needed: Google (sign-in), OpenAI via Emergent (AI drafting), PostHog (analytics), Resend (verification and password-reset emails), and our cloud hosting and MongoDB database provider. We may disclose information if required by law. We never sell your data.</p>
      </Section>

      <Section title="7. Data retention">
        <p>We keep your account and business content while your account is active. If you delete your account, we permanently remove your user record, businesses, drafts, assets, progress, AI session logs, feedback, and analytics events tied to your account. Backups are retained for up to 30 days and then overwritten. Aggregate, non-identifying statistics may be retained.</p>
      </Section>

      <Section title="8. Your rights and choices">
        <p><strong>Access and export.</strong> You can view and export your business assets (PDF, DOCX, Markdown) from the workspace at any time.</p>
        <p><strong>Deletion.</strong> You can permanently delete your account and all associated data yourself from the account menu (Delete account), or by emailing <a href={`mailto:${CONTACT}`} className="font-medium text-brand underline">{CONTACT}</a>. Deletion is irreversible.</p>
        <p><strong>Analytics opt-out.</strong> Decline in the consent banner, or contact us to opt out.</p>
        <p>Depending on where you live, you may have additional rights (such as access, correction, or portability). Contact us to exercise them.</p>
      </Section>

      <Section title="9. Security">
        <p>We protect your data with encryption in transit (HTTPS), secure httpOnly session cookies, bcrypt password hashing for email accounts, restricted access to production systems, and least-privilege service credentials. No method of transmission or storage is perfectly secure, but we work to protect your information.</p>
      </Section>

      <Section title="10. Children">
        <p>The Service is intended for people aged <strong>13 and older</strong>. We do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided us information, contact us and we will delete it.</p>
      </Section>

      <Section title="11. International users">
        <p>{COMPANY} is based in the United States and processes data there. By using the Service you understand your information may be processed in the United States.</p>
      </Section>

      <Section title="12. Changes to this Policy">
        <p>We may update this Policy. If we make material changes, we will update the effective date above and, where appropriate, notify you. Continued use after changes means you accept the updated Policy.</p>
      </Section>

      <Section title="13. Contact us">
        <p>Questions or requests? Email <a href={`mailto:${CONTACT}`} className="font-medium text-brand underline">{CONTACT}</a>.</p>
      </Section>
    </LegalShell>
  );
}

export function Terms() {
  useSeo({
    title: "Terms of Service | Launch to Income",
    description: "The terms governing your use of Launch to Income, including early access, content ownership, AI-generated content, and eligibility.",
    path: "/terms",
  });
  return (
    <LegalShell title="Terms of Service" testid="terms-page">
      <p>
        These Terms of Service ("Terms") are a legal agreement between you and {COMPANY} ("we," "us," or
        "our") governing your use of {PRODUCT} (the "Service"). By accessing or using the Service, you agree
        to these Terms. If you do not agree, do not use the Service.
      </p>

      <Section title="1. The Service">
        <p>{PRODUCT} is a guided workspace that helps you move from a business idea to your first sale through a step-by-step roadmap, with optional AI drafting assistance. Each step helps you create a real business asset you own.</p>
      </Section>

      <Section title="2. Eligibility">
        <p>You must be at least <strong>13 years old</strong> to use the Service. By using it, you confirm that you meet this requirement and that the information you provide is accurate.</p>
      </Section>

      <Section title="3. Your account">
        <p>You can sign in with Google or (where available) with an email and password. You are responsible for keeping your login secure and for activity under your account. Notify us at <a href={`mailto:${CONTACT}`} className="font-medium text-brand underline">{CONTACT}</a> if you suspect unauthorized use.</p>
      </Section>

      <Section title="4. Early access, payments, and refunds">
        <p>The Service is currently offered <strong>free of charge during early access</strong>. We do not collect payments and there are therefore <strong>no charges and no refunds</strong> at this time. If we introduce paid plans in the future, we will provide advance notice and separate terms, and anything you have already created will remain yours to keep and export.</p>
      </Section>

      <Section title="5. Your content and ownership">
        <p>You own the business content and assets you create with the Service. You grant us a limited license to store, process, and display that content solely to operate the Service for you (for example, saving your drafts and generating exports). We claim no ownership of your business ideas or assets.</p>
      </Section>

      <Section title="6. AI-generated content">
        <p>AI drafts are suggestions to help you. You review, edit, and decide what to keep, and you are the author of and responsible for the final content. AI output may be inaccurate or generic; verify anything important. We make <strong>no income or earnings guarantees</strong>, and you should not present any such guarantee in materials you create with the Service.</p>
      </Section>

      <Section title="7. Acceptable use">
        <p>Do not use the Service to break the law, infringe others' rights, upload harmful or unlawful content, attempt to disrupt or reverse-engineer the Service, or abuse the AI features. We may suspend or terminate accounts that violate these Terms.</p>
      </Section>

      <Section title="8. Availability and changes">
        <p>This is an evolving product in early access. Features may change, and the Service may occasionally be unavailable. We may modify or discontinue parts of the Service, and we will try to give reasonable notice of significant changes.</p>
      </Section>

      <Section title="9. Disclaimers">
        <p>The Service is provided "as is" and "as available," without warranties of any kind, whether express or implied, to the fullest extent permitted by law. We do not warrant that the Service will be uninterrupted, error-free, or that it will result in any particular business outcome.</p>
      </Section>

      <Section title="10. Limitation of liability">
        <p>To the fullest extent permitted by law, {COMPANY} will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for lost profits or revenues, arising from your use of the Service. Our total liability for any claim relating to the Service will not exceed one hundred U.S. dollars (US$100).</p>
      </Section>

      <Section title="11. Termination">
        <p>You may stop using the Service and delete your account at any time from the account menu. We may suspend or terminate access if you violate these Terms. On deletion, your data is removed as described in our Privacy Policy.</p>
      </Section>

      <Section title="12. Governing law">
        <p>These Terms are governed by the laws of the {JURISDICTION}, without regard to conflict-of-law rules. Any dispute will be resolved in the state or federal courts located in Charlotte (Mecklenburg County), North Carolina, and you consent to their jurisdiction.</p>
      </Section>

      <Section title="13. Changes to these Terms">
        <p>We may update these Terms from time to time. When we do, we will update the effective date above. Continued use of the Service after changes take effect means you accept the updated Terms.</p>
      </Section>

      <Section title="14. Contact">
        <p>Questions about these Terms? Email <a href={`mailto:${CONTACT}`} className="font-medium text-brand underline">{CONTACT}</a>.</p>
      </Section>
    </LegalShell>
  );
}

const FAQS = [
  { q: "What is Launch to Income?", a: "A guided workspace that takes you from a business idea to your first sale. Each of the seven steps leaves you with a real business asset you own, and AI can draft the tougher parts while you stay in control." },
  { q: "Is it really free?", a: "Yes. The product is free during early access. We don't collect payment details. If paid plans arrive later, you'll get advance notice and everything you've created stays yours." },
  { q: "Do I need a business idea already?", a: "No. If you only have a spark, we start at the very beginning by helping you clarify your purpose and spot an opportunity. If you're further along, we help you validate and sharpen your offer." },
  { q: "How does the AI work, and is it required?", a: "AI drafting is optional on every field. When you use it, we send the business context shown in the prompt to OpenAI to generate a draft you can edit or discard. You can complete every step by writing it yourself, and you're always the final author." },
  { q: "Is my data private?", a: "Yes. We use a privacy-first setup: analytics never records your screen and masks form inputs, and stays off until you consent. We don't sell your data or use your business content to train AI models. See our Privacy Policy for details." },
  { q: "Can I export my work?", a: "Anytime. You can export your business assets as PDF, DOCX, or Markdown from the workspace. Your work belongs to you." },
  { q: "How do I sign in?", a: "You can sign in with Google or with your email and password. Either way you land in your private workspace and pick up where you left off. If you register with email, we'll send a quick confirmation link to verify your address before you start." },
  { q: "How do I delete my account?", a: "Open the account menu in the sidebar and choose 'Delete account.' This permanently removes your account and all associated data. You can also email us to request deletion." },
  { q: "What if I get stuck on a step?", a: "Every step includes guidance, hints, and optional AI help. If you're still stuck, use 'Report a problem' below or email us and we'll help you move forward." },
];

export function Help() {
  useSeo({
    title: "Help & Support | Launch to Income",
    description: "Answers to common questions about Launch to Income, how the optional AI works, privacy, exports, and how to reach a real person.",
    path: "/help",
  });
  return (
    <LegalShell title="Help & Support" testid="help-page">
      <p>We want you to actually finish and make your first sale. Here's how to get answers and reach a real person.</p>

      <Section title="Frequently asked questions">
        <div className="space-y-4" data-testid="help-faqs">
          {FAQS.map((f, i) => (
            <details key={i} className="group rounded-xl border border-brand-line bg-white p-4 shadow-soft" data-testid={`faq-${i}`}>
              <summary className="flex cursor-pointer items-center gap-2 font-medium text-slate-900 marker:content-['']">
                <HelpCircle size={16} className="shrink-0 text-brand" /> {f.q}
              </summary>
              <p className="mt-2 pl-6 text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      <Section title="Contact support">
        <div className="grid gap-3 sm:grid-cols-2">
          <a
            data-testid="contact-support-btn"
            href={`mailto:${CONTACT}?subject=Launch%20to%20Income%20support`}
            className="flex items-start gap-3 rounded-xl border border-brand-line bg-white p-4 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lift"
          >
            <Mail size={18} className="mt-0.5 text-brand" />
            <span>
              <span className="block font-medium text-slate-900">Email support</span>
              <span className="text-slate-500">{CONTACT}</span>
            </span>
          </a>
          <a
            data-testid="report-problem-btn"
            href={`mailto:${CONTACT}?subject=Problem%20report%3A%20Launch%20to%20Income&body=What%20happened%3A%0A%0AWhat%20I%20expected%3A%0A%0APage%2Fstep%3A`}
            className="flex items-start gap-3 rounded-xl border border-brand-line bg-white p-4 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lift"
          >
            <AlertTriangle size={18} className="mt-0.5 text-amber-500" />
            <span>
              <span className="block font-medium text-slate-900">Report a problem</span>
              <span className="text-slate-500">Tell us what broke and we'll fix it</span>
            </span>
          </a>
        </div>
      </Section>

      <Section title="Response times">
        <p className="flex items-center gap-2"><Clock size={16} className="text-brand" /> We aim to reply to every message within <strong>2 business days</strong>, and usually sooner during early access.</p>
      </Section>

      <Section title="Who's behind Launch to Income">
        <p className="flex items-start gap-2">
          <User size={16} className="mt-0.5 text-brand" />
          <span>{PRODUCT} is built and operated by {COMPANY}, based in Charlotte, North Carolina. We're a small team that believes starting a business shouldn't be a thousand confusing decisions, so we turned it into one clear next step at a time.</span>
        </p>
      </Section>
    </LegalShell>
  );
}
